import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useI18n } from '../i18n/I18nContext';
import { NavBar } from '../components/NavBar';
import { BackToHome } from '../components/BackToHome';
import { KeyRound, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const { t } = useI18n();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError(t('reset.short_password'));
      return;
    }
    if (password !== confirm) {
      setError(t('reset.mismatch'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || t('reset.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <NavBar />
      <BackToHome />
      <div className="max-w-md mx-auto px-4 py-10">
        <div className="bg-white rounded-2xl shadow-md border border-slate-100 p-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
              <KeyRound className="w-6 h-6 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">{t('reset.heading')}</h1>
            <p className="text-sm text-slate-600 mt-2">{t('reset.subtitle')}</p>
          </div>

          {!token && (
            <div data-testid="reset-no-token" className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm">
              {t('reset.no_token')}
            </div>
          )}

          {done ? (
            <div data-testid="reset-done" className="text-center">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <p className="text-green-800 font-medium">{t('reset.done')}</p>
              <p className="text-sm text-slate-500 mt-2">{t('reset.redirecting')}</p>
            </div>
          ) : token ? (
            <form data-testid="reset-form" onSubmit={onSubmit} className="space-y-4">
              {error && (
                <div data-testid="reset-error" className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('reset.new_password')}</label>
                <input data-testid="reset-password-input" type="password" required minLength="6"
                       value={password} onChange={(e) => setPassword(e.target.value)}
                       className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
                       placeholder="•••••••" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('reset.confirm_password')}</label>
                <input data-testid="reset-confirm-input" type="password" required minLength="6"
                       value={confirm} onChange={(e) => setConfirm(e.target.value)}
                       className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
                       placeholder="•••••••" />
              </div>
              <button data-testid="reset-submit-btn" type="submit" disabled={loading}
                      className="w-full px-6 py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 disabled:opacity-50">
                {loading ? t('reset.submitting') : t('reset.submit')}
              </button>
            </form>
          ) : null}

          <div className="mt-6 text-center">
            <Link to="/login" data-testid="link-back-to-login" className="text-sm text-slate-600 hover:text-blue-600">
              {t('forgot.back_to_login')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
